import type { IDayJSType } from '~/types/dayjs';
import type { Dayjs } from '~/utils/dayjs';

import { dayjs, dayjsTz } from '~/utils/dayjs';

export const formatDateBy = (format: string, date?: string | Date) => {
  if (!date) date = new Date();

  return dayjsTz(date).format(format) ?? '-';
};
export const lastTimestampAt = (time?: string | null) => {
  if (!time) {
    return;
  }
  return dayjsTz(new Date()).to(dayjsTz(time));
};

/**
 * Formats the duration between a start date and an end date (or now) as a human-readable string.
 * Seconds are only shown if days, hours, and minutes are all zero.
 * @param startedAt - The start date as an ISO string (e.g., '2025-05-10T12:00:00Z')
 * @param completedAt - The end date as an ISO string (optional, defaults to now)
 * @returns A string like '2 days 5 hours 10 minutes', '45 seconds' when minutes/hours/days are zero,
 *          '-' for invalid/negative durations, or '0 minutes' for zero duration
 */
export const formatDurationDate = (startedAt: string, completedAt?: string): string => {
  const start: Dayjs = dayjsTz(startedAt);
  const end: Dayjs = completedAt ? dayjsTz(completedAt) : dayjsTz();

  if (!start.isValid() || (completedAt && !end.isValid())) {
    return '-';
  }

  if (start.isAfter(end)) {
    return '-';
  }

  // Calculate duration components
  const duration = end.diff(start, 'second');
  if (duration === 0) {
    return '0 minutes';
  }

  const days: number = end.diff(start, 'day');
  const hours: number = end.diff(start.add(days, 'day'), 'hour');
  const minutes: number = end.diff(start.add(days, 'day').add(hours, 'hour'), 'minute');
  const seconds = end.diff(
    start.add(days, 'day').add(hours, 'hour').add(minutes, 'minute'),
    'second',
  );

  // const days = Math.floor(duration / (24 * 60 * 60));
  // const remainingAfterDays = duration % (24 * 60 * 60);
  // const hours = Math.floor(remainingAfterDays / (60 * 60));
  // const remainingAfterHours = remainingAfterDays % (60 * 60);
  // const minutes = Math.floor(remainingAfterHours / 60);
  // const seconds = remainingAfterHours % 60;

  // Build result
  const result: string[] = [];
  if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours > 0) result.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  if (minutes > 0) result.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
  if (days === 0 && hours === 0 && minutes === 0 && seconds > 0) {
    result.push(`${seconds} second${seconds > 1 ? 's' : ''}`);
  }

  return result.length > 0 ? result.join(' ') : '0 minutes';
};

/**
 * Show date by local timezone
 * @param numberOfDays
 * @returns Date
 */
export const getDateByNumberOfNextDays = (numberOfDays: number): Date => {
  const now = dayjs();
  const target = dayjs(now).add(numberOfDays, 'day');
  return target.toDate();
};

/**
 * Format date in ISO format
 * @param date
 * @param format
 * @example
 * formatDateIso('2025-01-01T00:00:00Z', 'YYYY-MM-DD') // '2025-01-01'
 * formatDateIso('2025-01-01T00:00:00+07:00', 'MMM YYYY') // 'Jan 2025'
 * formatDateIso('2025-01-01T00:00:00-05:00', 'MMM DD, YYYY') // 'Jan 01, 2025'
 * @returns string
 */
export const formatDateIso = (date: IDayJSType, format: string): string => {
  return dayjs(date).tz('UTC').format(format);
};
